import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GenericCard } from "./GenericCard";
import { api, transformInserat } from "../services/api";

const OfferCardDeck = ({ className = "" }) => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const strapiInserate = await api.getInserate();
        const transformedInserate = strapiInserate
          .map(transformInserat)
          .filter(Boolean)
          .slice(0, 3); // Get only first 3 offers
        setOffers(transformedInserate);
      } catch (err) {
        console.error("Error fetching offers:", err);
        setError("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const handleCardClick = (offerId) => {
    navigate(`/aktuelle-angebote/${offerId}`);
  };

  if (loading) {
    return (
      <div
        className={`relative w-full max-w-md mx-auto min-h-[480px] ${className}`}
      >
        {/* Loading placeholders stacked */}
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="absolute top-0 bg-gray-200 rounded-lg shadow-lg animate-pulse h-[480px]"
            style={{
              left: "32px",
              right: "32px",
              transform: `rotate(${(index - 1) * 3}deg) translateY(${
                index * 4
              }px)`,
              zIndex: 3 - index,
            }}
          />
        ))}
      </div>
    );
  }

  if (error || offers.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative w-full max-w-md mx-auto min-h-[480px] perspective-1000 ${className}`}
    >
      {offers.map((offer, index) => {
        const rotation = (index - 1) * 4; // Slightly more rotation for better effect
        const translateY = index * 6; // More vertical offset
        const translateX = (index - 1) * 2; // Slight horizontal offset

        const isTopCard = index === 0; // Only the first card (index 0) is the top card

        return (
          <div
            key={offer.id}
            className={`absolute top-0 transition-all duration-500 ${
              isTopCard
                ? "cursor-pointer hover:scale-105 hover:-translate-y-2"
                : "cursor-default"
            }`}
            style={{
              left: "20px",
              right: "20px",
              transform: `rotate(${rotation}deg) translateY(${translateY}px) translateX(${translateX}px)`,
              zIndex: 3 - index,
              transformOrigin: "center bottom",
              pointerEvents: isTopCard ? "auto" : "none",
            }}
            onClick={isTopCard ? () => handleCardClick(offer.id) : undefined}
            onMouseEnter={
              isTopCard
                ? (e) => {
                    // Add subtle lift effect on hover
                    e.currentTarget.style.transform = `rotate(${rotation}deg) translateY(${
                      translateY - 8
                    }px) translateX(${translateX}px) scale(1.02)`;
                  }
                : undefined
            }
            onMouseLeave={
              isTopCard
                ? (e) => {
                    // Return to original position
                    e.currentTarget.style.transform = `rotate(${rotation}deg) translateY(${translateY}px) translateX(${translateX}px)`;
                  }
                : undefined
            }
          >
            <div className="w-full shadow-2xl rounded-sm overflow-hidden   relative">
              {!isTopCard && (
                <div className="absolute inset-0 bg-gray-700 bg-opacity-30 z-10 rounded-sm"></div>
              )}
              <GenericCard
                images={offer.images}
                headline={offer.title}
                info={[
                  { label: "BJ", value: offer.year },
                  {
                    label: "KM",
                    value: `${offer.mileage.toLocaleString()} km`,
                  },
                ]}
                price={
                  offer.price > 0 ? `${offer.price.toLocaleString()} €` : null
                }
                buttonText="Details"
                onButtonClick={
                  isTopCard ? () => handleCardClick(offer.id) : undefined
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OfferCardDeck;
